import { useMemo, useCallback, useEffect, useRef } from 'react';
import {
  expandAllCategories,
  collectCategoryIds,
  TCategoryPredicate,
  defaultCategoryPredicate,
} from '@/pages/strategic/utils/tableExpandUtils';

export interface UseExpandCollapseOptions {
  /** Currently expanded keys (from external state) */
  expandedKeys: string[];
  /** State setter for expandedKeys */
  setExpandedKeys: React.Dispatch<React.SetStateAction<string[]>>;
  /** Ref that stays in sync with expandedKeys */
  expandedKeysRef: React.MutableRefObject<string[]>;
  /** The table data array (items with `children` arrays) */
  data: any[];
  /** Predicate to identify which items are expandable categories (default: defaultCategoryPredicate) */
  predicate?: TCategoryPredicate;
  /** Keys that should always stay expanded and should not be treated as toggleable targets */
  fixedExpandedKeys?: string[];
  /** Optional override for toggleable keys (defaults to collectCategoryIds(data, predicate)) */
  toggleableKeys?: string[];
  /** Optional row-level buried point callback (expand/collapse by row click) */
  onTrackRowToggle?: (action: 'expand' | 'collapse', key: string) => void;
  /** Optional expand-all buried point callback */
  onTrackExpandAllToggle?: (nextExpanded: boolean) => void;
}

export interface UseExpandCollapseReturn {
  /** Whether all expandable categories in the data are currently expanded */
  isAllExpanded: boolean;
  /** Toggle between expand all / collapse all */
  toggleExpandAll: () => void;
  /** Stable toggle callback for header-cached render functions */
  stableToggleExpandAll: () => void;
  /** Expand all categories with children in the given data */
  expandAllCategoriesWithChildren: (data: any[]) => void;
  /** Internal diff-aware expanded key handler with optional tracking switch */
  handleExpandedKeysChangeInternal: (keys: string[], shouldTrack?: boolean) => void;
  /** Wrapper for table expandable.onExpandedKeysChange */
  handleExpandedKeysChange: (keys: string[], context?: any) => void;
}

/**
 * Reusable hook for expand/collapse all functionality in tables with nested data.
 *
 * Accepts externally-managed `expandedKeys` state and provides computed
 * `isAllExpanded` flag, `toggleExpandAll` action, and `expandAllCategoriesWithChildren`
 * utility — ready to plug into `tea-component` Table's `expandable` addon.
 *
 * @param options - Configuration including external state and data
 * @returns Expand/collapse computed values and actions
 *
 * @example
 * ```tsx
 * // In your component — declare expandedKeys state early
 * const [expandedKeys, setExpandedKeys] = useState(['2']);
 * const expandedKeysRef = useRef<string[]>(['2']);
 *
 * // ... later, after data is available
 * const {
 *   isAllExpanded,
 *   toggleExpandAll,
 *   expandAllCategoriesWithChildren,
 * } = useExpandCollapse({
 *   expandedKeys,
 *   setExpandedKeys,
 *   expandedKeysRef,
 *   data: tableData,
 * });
 * ```
 */
export const useExpandCollapse = (options: UseExpandCollapseOptions): UseExpandCollapseReturn => {
  const {
    expandedKeys,
    setExpandedKeys,
    expandedKeysRef,
    data,
    predicate = defaultCategoryPredicate,
    fixedExpandedKeys = [],
    toggleableKeys,
    onTrackRowToggle,
    onTrackExpandAllToggle,
  } = options;
  const isExpandAllActionRef = useRef(false);

  // Expand all categories that match the predicate
  const expandAllCategoriesWithChildren = useCallback((sourceData: any[]) => {
    expandAllCategories(sourceData, setExpandedKeys, predicate, expandedKeysRef);
  }, [setExpandedKeys, predicate, expandedKeysRef]);

  const resolvedToggleableKeys = useMemo(() => {
    if (Array.isArray(toggleableKeys)) {
      return toggleableKeys;
    }
    if (!data || data.length === 0) {
      return [];
    }
    return collectCategoryIds(data, predicate);
  }, [data, predicate, toggleableKeys]);

  // Keep ref in sync with external state to avoid stale-closure compares in handlers.
  useEffect(() => {
    expandedKeysRef.current = expandedKeys;
  }, [expandedKeys, expandedKeysRef]);

  // Check if all expandable categories are currently expanded
  const isAllExpanded = useMemo(() => {
    if (resolvedToggleableKeys.length === 0) return false;
    return resolvedToggleableKeys.every((key: string) => expandedKeys.includes(key));
  }, [expandedKeys, resolvedToggleableKeys]);

  // Diff-aware expanded key update; supports buried-point callbacks.
  const handleExpandedKeysChangeInternal = useCallback((keys: string[], shouldTrack: boolean = true) => {
    const merged = [...new Set([...(keys || []), ...(fixedExpandedKeys || [])])];
    const previousKeys = expandedKeysRef.current || [];
    const previousKeysSorted = [...previousKeys].sort().join(',');
    const mergedKeysSorted = [...merged].sort().join(',');
    const keysChanged = previousKeysSorted !== mergedKeysSorted;

    if (!keysChanged) {
      setExpandedKeys(merged);
      expandedKeysRef.current = merged;
      isExpandAllActionRef.current = false;
      return;
    }

    // Track row-level expand/collapse only for row interactions (not expand-all).
    if (shouldTrack && !isExpandAllActionRef.current && onTrackRowToggle) {
      const toggleableKeySet = new Set(resolvedToggleableKeys);
      const newlyExpanded = merged.filter(key => !previousKeys.includes(key) && toggleableKeySet.has(key));
      const newlyCollapsed = previousKeys.filter(key => !merged.includes(key) && toggleableKeySet.has(key));
      newlyExpanded.forEach(key => onTrackRowToggle('expand', key));
      newlyCollapsed.forEach(key => onTrackRowToggle('collapse', key));
    }

    setExpandedKeys(merged);
    expandedKeysRef.current = merged;
    isExpandAllActionRef.current = false;
  }, [expandedKeysRef, fixedExpandedKeys, onTrackRowToggle, resolvedToggleableKeys, setExpandedKeys]);

  const handleExpandedKeysChange = useCallback((keys: string[], context?: any) => {
    handleExpandedKeysChangeInternal(keys, true);
  }, [handleExpandedKeysChangeInternal]);

  // Toggle expand/collapse all
  const toggleExpandAll = useCallback(() => {
    const nextExpanded = !isAllExpanded;
    if (onTrackExpandAllToggle) {
      onTrackExpandAllToggle(nextExpanded);
    }

    const nextKeys = nextExpanded
      ? [...new Set([...(resolvedToggleableKeys || []), ...(fixedExpandedKeys || [])])]
      : [...(fixedExpandedKeys || [])];
    isExpandAllActionRef.current = true;
    setExpandedKeys(nextKeys);
    expandedKeysRef.current = nextKeys;
  }, [
    expandedKeysRef,
    fixedExpandedKeys,
    isAllExpanded,
    onTrackExpandAllToggle,
    resolvedToggleableKeys,
    setExpandedKeys,
  ]);

  // Stable callback for header-cached render paths.
  const toggleExpandAllRef = useRef(toggleExpandAll);
  toggleExpandAllRef.current = toggleExpandAll;
  const stableToggleExpandAll = useCallback(() => {
    toggleExpandAllRef.current();
  }, []);

  return {
    isAllExpanded,
    toggleExpandAll,
    stableToggleExpandAll,
    expandAllCategoriesWithChildren,
    handleExpandedKeysChangeInternal,
    handleExpandedKeysChange,
  };
};

export default useExpandCollapse;
